/**
 * PATH: packages/email-hub/src/feature/email-layout/useMobileNavigation.ts
 * FILE_ID: EMAIL-LAYOUT-MOBILE-NAV-001
 * PURPOSE: Preserve EmailLayout's viewport, host-dock, and drawer behavior.
 */

import { useEffect, useState } from 'react'
import { useHostBottomDock } from '../../_internal/hooks/useHostBottomDock'
import { useIsNarrowViewport } from '../../_internal/hooks/useIsNarrowViewport'
import { useUiStore, type EmailNavPosition } from '../../_internal/stores/uiStore'
import { isEmailDetailPathname } from '../lib/emailRoutes'

export function useMobileNavigation(
  pathname: string,
  navPosition: EmailNavPosition,
  userCollapsed: boolean,
) {
  const isNarrow = useIsNarrowViewport()
  // Embedded in the dashboard, the host MobileDock is fixed at the viewport
  // bottom and paints over the hub's own bottom folder bar. The host
  // publishes `--mobile-dock-h`; standalone email.quotech.ai never does.
  const hostBottomDock = useHostBottomDock()
  const useBottomNav = !isNarrow && navPosition === 'bottom' && !hostBottomDock
  const hostDockReplacesNav = hostBottomDock && navPosition === 'bottom'

  // Publish the Email Hub bottom bar height so the global UniversalDock can
  // lift itself above it. Clear it whenever bottom navigation is inactive.
  useEffect(() => {
    const root = document.documentElement
    if (useBottomNav) {
      root.style.setProperty('--email-hub-bottom-bar-h', '48px')
      return () => {
        root.style.removeProperty('--email-hub-bottom-bar-h')
      }
    }
    root.style.removeProperty('--email-hub-bottom-bar-h')
    return undefined
  }, [useBottomNav])

  const navOnRight = !isNarrow && navPosition === 'right'
  const emailMobileExclusiveView = useUiStore((s) => s.emailMobileExclusiveView)
  const isEmailDetailRoute = isEmailDetailPathname(pathname)
  // A host dock makes this strip the only discoverable folder navigation, so
  // it remains visible at every width and while reading.
  const showMobileFolderStrip = hostDockReplacesNav
    ? true
    : isNarrow && !emailMobileExclusiveView && !isEmailDetailRoute
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const collapsed = isNarrow ? false : userCollapsed

  // Auto-close the drawer whenever the route changes.
  useEffect(() => {
    if (mobileNavOpen) setMobileNavOpen(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  // Escape closes the drawer.
  useEffect(() => {
    if (!mobileNavOpen) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMobileNavOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [mobileNavOpen])

  return {
    isNarrow,
    hostBottomDock,
    useBottomNav,
    hostDockReplacesNav,
    navOnRight,
    showMobileFolderStrip,
    mobileNavOpen,
    setMobileNavOpen,
    collapsed,
  }
}
